/**
 * Reiter „Abrechnen“ auf `/vorgaenge/:id/abrechnung` (Abrechnung nach der Veranstaltung, Stufe 1 + 2).
 * Vorlage: ~/maestro-uebergabe/mockup-abrechnung.html (Antoine, 28.09.2026 freigegeben), Zustände 1–14.
 *
 * Aufbau: links Hinweise, Tor 2 (Rechnungsmail), die Zeilenliste und die Zahlungen; rechts die
 * Summenkarte und der Abschluss bzw. die Rechnung. Am Handy drei eigene Reiter (Zeilen, Zahlungen,
 * Abschluss) und unten fest die Summenleiste mit dem einen Knopf.
 *
 * Jede Zahl kommt vom Server (`GET /api/events/:id/abrechnung`, gerechnet in packages/pricing).
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { heuteInZone } from "@maestro/feiertage";
import { euroZahl } from "@maestro/pricing";
import { apiMutate, apiMutateMitZusatz, useApi } from "../../hooks/useApi";
import { vorgangPfad } from "../../lib/pfade";
import type { ApiError } from "../../lib/api";
import { alsEingabe, anteilVorschau, einfuegeIndexRabatt, istBetragszeile, umordnungsHinweis, mengeText, prozentText, rabattUnterzeile, satzText, wirdRabattiert, zahlungsPille, zahlweise, zeilenTitel } from "../../lib/abrechnung-anzeige";
import { Aufklapper, Button, ButtonLink, Checkbox, DateInput, EmptyState, ErrorNote, Icon, Input, ListenReiter, LoadingRows, MenueEintrag, Modal, Radio, StatusPill, Textarea, useBestaetigung, useToastWennVorhanden } from "../components";
import { PILL_CLASSES, formatDate, formatDateTime, money, parseGermanEurosToCents } from "../format";
import { AusserhalbDrawer, BetragsDrawer, RabattDrawer, Segmente, VorschauDrawer } from "./AbrechnungDrawer";
import type { AbrAntwort, AbrZeile, AbrZeileEingabe, VorschauDaten } from "./typen";

const T = {
  karte: "rounded-[14px] border border-[#EFE8DA] bg-white shadow-[0_8px_18px_-10px_rgba(45,38,26,.14)] dark:border-outline/30 dark:bg-night-card dark:shadow-none",
  kopf: "flex flex-wrap items-center gap-2 border-b border-[#F2ECDF] px-[18px] py-[13px] dark:border-outline/30",
  titel: "font-title-md text-[14.5px] font-semibold text-on-background dark:text-inverse-on-surface",
  zeile: "border-b border-[#F6F0E4] dark:border-outline/20",
  leise: "text-[11.5px] text-secondary dark:text-secondary-fixed-dim",
  chip: "inline-flex h-[18px] items-center rounded-[5px] px-[6px] text-[10.5px] font-semibold",
  betrag: "tabular-nums font-semibold text-on-background dark:text-inverse-on-surface",
  hinweisWarn: "rounded-[14px] border border-outline-variant bg-[#F0EAE0] px-4 py-3 text-[#6F675A] dark:border-white/10 dark:bg-white/[0.08] dark:text-[#b8ae9e]",
  hinweisGefahr: "rounded-[12px] border border-[#EFC9C2] bg-[#F8E7E3] px-4 py-3 text-[#8F2F28] dark:border-[#B23B32]/40 dark:bg-[#B23B32]/15 dark:text-[#e39b92]",
  hinweisInfo: "rounded-[14px] border border-outline-variant bg-[#F0EAE0] px-4 py-3 text-[#6F675A] dark:border-white/10 dark:bg-white/[0.08] dark:text-[#b8ae9e]",
} as const;

const fehlerText = (e: unknown) => (e instanceof Error ? e.message : String(e));

function Chip({ ton, children }: { ton: keyof typeof PILL_CLASSES; children: ReactNode }) {
  return <span className={`${T.chip} ${PILL_CLASSES[ton]}`}>{children}</span>;
}

export function AbrechnenReiter({ eventId, onGeaendert }: { eventId: string; onGeaendert?: () => void }) {
  // Stufe 2: eine ältere Version lässt sich ansehen (nur lesen); null = aktuelle Version.
  const [ansicht, setAnsicht] = useState<number | null>(null);
  const q = useApi<AbrAntwort>(`/api/events/${eventId}/abrechnung${ansicht != null ? `?version=${ansicht}` : ""}`);
  const [stand, setStand] = useState<AbrAntwort | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [fehler, setFehler] = useState<{ text: string; code?: string; kundeId?: string } | null>(null);
  const [gespeichertAm, setGespeichertAm] = useState<string | null>(null);
  const [drawer, setDrawer] = useState<
    | { art: "rabatt"; zeile: AbrZeile | null; zuschlag?: boolean }
    | { art: "betrag"; zeile: AbrZeile | null; typ: "leistung" | "mehrverbrauch"; wo?: "ueber" | "unter" }
    | { art: "ausserhalb" }
    | { art: "vorschau" }
    | null
  >(null);
  const [vorschau, setVorschau] = useState<{ daten: VorschauDaten | null; fehler: string | null; laden: boolean }>({ daten: null, fehler: null, laden: false });
  const [mTab, setMTab] = useState<"zeilen" | "zahlungen" | "abschluss">("zeilen");
  const [korrekturOffen, setKorrekturOffen] = useState(false);
  // Stufe 3: Ergebnis der Link-Deaktivierung beim Abschluss (steht zusätzlich im Verlauf).
  const [linkErgebnis, setLinkErgebnis] = useState<{ deaktiviert: { id: string; amountCents: number }[]; fehler: string[]; hinweise?: string[] } | null>(null);
  const { bestaetigen, dialog } = useBestaetigung();
  const angelegt = useRef(false);
  const zahlungenRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (q.data) setStand(q.data);
  }, [q.data]);
  const zeigeVersion = (v: number | null) => {
    setStand(null);
    setFehler(null);
    setAnsicht(v);
  };

  // „Zur Abrechnung“ legt den Entwurf an, vorbelegt aus der angenommenen Fassung (Mockup, Schritt 1).
  useEffect(() => {
    if (!stand || stand.abrechnung || !stand.abrechenbar || angelegt.current) return;
    angelegt.current = true;
    void rufe("anlegen", "POST", "", {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stand]);

  async function rufe(was: string, methode: "GET" | "POST" | "PUT", pfad: string, body?: unknown): Promise<AbrAntwort | null> {
    setBusy(was);
    setFehler(null);
    try {
      const r = await apiMutate<AbrAntwort>(`/api/events/${eventId}/abrechnung${pfad}`, methode, body);
      setStand(r);
      setGespeichertAm(new Date().toISOString());
      onGeaendert?.();
      return r;
    } catch (e) {
      const a = e as ApiError;
      const b = a.body as { kundeId?: string } | undefined;
      setFehler({ text: fehlerText(e), code: a.code, kundeId: b?.kundeId });
      return null;
    } finally {
      setBusy(null);
    }
  }

  const speichereZeilen = (zeilen: AbrZeileEingabe[]) => rufe("zeilen", "PUT", "", { zeilen });

  if (q.loading && !stand) return <LoadingRows rows={6} />;
  if (q.error && !stand) return <ErrorNote message={q.error} />;
  if (!stand) return null;
  if (!stand.abrechnung) {
    if (!stand.abrechenbar) return <EmptyState icon="receipt_long" title="Abrechnen geht nach der Zusage" hint="Sobald der Gast zugesagt hat (Zugesagt, Bezahlt oder Durchgeführt), legt MAESTRO hier die Abrechnung aus dem Angebot an." />;
    return fehler ? <ErrorNote message={fehler.text} /> : <LoadingRows rows={6} />;
  }

  const abr = stand.abrechnung;
  const nurLesen = Boolean(stand.nurLesen);
  const gesperrt = abr.status === "abgeschlossen" || nurLesen;
  const aktuelleVersion = stand.versionen?.[stand.versionen.length - 1]?.version ?? abr.version;
  const leitung = stand.darfAbschliessen;
  const offen = stand.offenePunkte.length;
  const ueber = gesperrt ? (stand.ueberzahlungZuEntscheidenCents ?? 0) : (stand.saldo?.ueberzahlungCents ?? 0);
  const rechnung = stand.rechnung;

  const anlegenStarten = async () => {
    setDrawer({ art: "vorschau" });
    setVorschau({ daten: null, fehler: null, laden: true });
    try {
      const d = await apiMutate<VorschauDaten>(`/api/events/${eventId}/abrechnung/vorschau`, "GET");
      setVorschau({ daten: d, fehler: null, laden: false });
    } catch (e) {
      setVorschau({ daten: null, fehler: fehlerText(e), laden: false });
    }
  };
  const inLexofficeAnlegen = async () => {
    setBusy("abschliessen");
    setFehler(null);
    try {
      const r = await apiMutateMitZusatz<AbrAntwort, { links?: { deaktiviert: { id: string; amountCents: number }[]; fehler: string[]; hinweise?: string[] } }>(`/api/events/${eventId}/abrechnung/abschliessen`, "POST", {});
      setStand(r.data);
      setLinkErgebnis(r.links ?? null);
      setGespeichertAm(new Date().toISOString());
      onGeaendert?.();
      setMTab("abschluss");
    } catch (e) {
      const a = e as ApiError;
      const b = a.body as { kundeId?: string } | undefined;
      setFehler({ text: fehlerText(e), code: a.code, kundeId: b?.kundeId });
    } finally {
      setBusy(null);
      setDrawer(null);
    }
  };
  const ungewissFreigeben = async () => {
    if (!rechnung) return;
    const ok = await bestaetigen({ titel: "Wirklich freigeben?", text: "Nur freigeben, wenn die Rechnung in LexOffice nachweislich NICHT existiert. Danach lässt sich die Abrechnung erneut abschließen.", bestaetigen: "Nicht vorhanden – freigeben" });
    if (!ok) return;
    setBusy("freigeben");
    try {
      await apiMutate(`/api/invoices/${rechnung.id}/ungewiss-freigeben`, "POST", { inLexofficeNichtVorhanden: true });
      q.reload();
    } catch (e) {
      setFehler({ text: fehlerText(e) });
    } finally {
      setBusy(null);
    }
  };

  const korrigieren = async (grund: string) => {
    const r = await rufe("korrigieren", "POST", "/korrigieren", { grund });
    if (r) {
      setKorrekturOffen(false);
      setMTab("zeilen");
    }
  };

  const summenKarte = <SummenKarte stand={stand} />;
  const abschluss = gesperrt ? (
    <RechnungsKarte stand={stand} onMail={() => document.getElementById("abr-mail")?.scrollIntoView({ behavior: "smooth" })} onKorrigieren={() => setKorrekturOffen(true)} />
  ) : (
    <AbschlussKarte stand={stand} busy={busy === "abschliessen"} onAusserhalb={() => setDrawer({ art: "ausserhalb" })} onUeberzahlung={() => { setMTab("zahlungen"); zahlungenRef.current?.scrollIntoView({ behavior: "smooth" }); }} onAbschliessen={anlegenStarten} />
  );

  return (
    <div className="pb-24 lg:pb-0">
      {dialog}
      <div className="mb-4 lg:hidden">
        <ListenReiter
          label="Abrechnen"
          aktiv={mTab}
          onWahl={setMTab}
          reiter={[
            { key: "zeilen", label: "Zeilen", anzahl: stand.zeilen.length },
            ...(nurLesen ? [] : [{ key: "zahlungen" as const, label: "Zahlungen", anzahl: stand.eingaenge.length }]),
            { key: "abschluss", label: "Abschluss", anzahl: gesperrt ? 0 : offen },
          ]}
        />
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <p className={`flex items-center gap-1.5 text-[12px] ${gesperrt ? "text-on-surface-variant" : "text-[#6F675A] dark:text-[#b8ae9e]"}`} data-testid="abr-stand">
          <Icon name={gesperrt ? "lock" : "check_circle"} className="text-[15px]" />
          {gesperrt ? `Version ${abr.version} · abgeschlossen ${formatDate(abr.abgeschlossenAt)}` : `${abr.version > 1 ? `Version ${abr.version} · ` : ""}Entwurf · gespeichert${gespeichertAm ? ` ${formatDateTime(gespeichertAm).split(", ").pop()}` : ""}`}
        </p>
        {(stand.versionen?.length ?? 0) > 1 && (
          <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Version wählen" data-testid="abr-versionen">
            {stand.versionen!.map((v) => {
              const aktiv = v.version === abr.version;
              return (
                <button
                  key={v.version}
                  type="button"
                  aria-pressed={aktiv}
                  onClick={() => !aktiv && zeigeVersion(v.version === aktuelleVersion ? null : v.version)}
                  className={`rounded-full border px-2.5 py-0.5 text-[11.5px] font-semibold ${aktiv ? "border-primary bg-primary/10 text-primary" : "border-outline-variant/60 text-on-surface-variant hover:border-primary dark:border-outline/40 dark:text-secondary-fixed-dim"}`}
                >
                  V{v.version}{v.version === aktuelleVersion ? " · aktuell" : ""}
                </button>
              );
            })}
          </div>
        )}
      </div>
      {nurLesen && (
        <div className={`${T.hinweisInfo} mb-4 flex flex-wrap items-center gap-3`} data-testid="abr-nur-lesen">
          <Icon name="history" className="text-[18px]" />
          <p className="min-w-0 flex-1 text-[12.5px]"><b>Version {abr.version}</b> ist durch Version {aktuelleVersion} abgelöst und nur zum Nachsehen. Ihre Rechnung wurde bzw. wird beim Abschluss der neuen Version per Gutschrift storniert.</p>
          <Button variant="secondary" className="!py-1.5" onClick={() => zeigeVersion(null)}>Zur aktuellen Version</Button>
        </div>
      )}
      {!nurLesen && abr.version > 1 && abr.status === "entwurf" && (
        <div className={`${T.hinweisInfo} mb-4`} data-testid="abr-korrektur-hinweis">
          <p className="flex items-center gap-2 text-[13px] font-semibold"><Icon name="edit_note" className="text-[18px]" /> Korrektur: Version {abr.version} der Abrechnung</p>
          <p className="mt-0.5 text-[12.5px]">
            Alles ist aus Version {abr.version - 1} übernommen und änderbar.{stand.stornoBeimAbschluss ? ` Beim Abschließen storniert MAESTRO die Rechnung ${stand.stornoBeimAbschluss.invoiceNumber ?? ""} per Gutschrift und legt die neue an; beide gehen nach deiner Freigabe zusammen an den Gast.` : ""}
            {stand.eingaenge.some((e) => e.quelle === "guthaben" && e.version != null && !e.rueckerstattung) ? " Guthaben aus der früheren Version ist als Zahlungsverrechnung übernommen, nicht neu gebucht." : ""}
          </p>
        </div>
      )}
      {!nurLesen && (stand.fruehereRueckerstattungen ?? []).map((r) => (
        <div key={r.version} className={`${T.hinweisWarn} mb-4`} data-testid="abr-fruehere-rueckerstattung">
          <p className="flex items-center gap-2 text-[13px] font-semibold"><Icon name="undo" className="text-[18px]" /> In Version {r.version} war eine Rückerstattung über {money(r.cents)} entschieden</p>
          <p className="mt-0.5 text-[12.5px]">{(r.abgezogenCents ?? 0) > 0 ? `Sie steht hier als Abgang (${money(r.abgezogenCents ?? 0)}), damit dieselbe Überzahlung nicht ein zweites Mal erstattet wird. Eine inzwischen über Stripe ausgeführte Erstattung zählt MAESTRO an der Zahlung selbst und zieht sie hier nicht doppelt ab.` : "Die Erstattung ist bereits an der Zahlung erfasst und in den Zahlungen enthalten."}</p>
        </div>
      ))}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
        <div className="min-w-0 space-y-5">
          {fehler && <FehlerBlock fehler={fehler} onRetry={fehler.code === "lexoffice_nicht_erreichbar" || fehler.code === "storno_fehlgeschlagen" ? anlegenStarten : undefined} />}
          {linkErgebnis && (linkErgebnis.deaktiviert.length > 0 || linkErgebnis.fehler.length > 0) && (
            <div className={linkErgebnis.fehler.length ? T.hinweisWarn : T.hinweisInfo} role="status" data-testid="abr-links-deaktiviert">
              <p className="flex items-center gap-2 text-[13px] font-semibold"><Icon name="link_off" className="text-[18px]" /> {linkErgebnis.deaktiviert.length > 0 ? `${linkErgebnis.deaktiviert.length === 1 ? "Ein alter Zahlungslink" : `${linkErgebnis.deaktiviert.length} alte Zahlungslinks`} deaktiviert (${linkErgebnis.deaktiviert.map((d) => money(d.amountCents)).join(", ")})` : "Alte Zahlungslinks nicht deaktiviert"}</p>
              {linkErgebnis.fehler.map((f) => <p key={f} className="mt-0.5 text-[12.5px]">{f}</p>)}
              {(linkErgebnis.hinweise ?? []).map((f) => <p key={f} className="mt-0.5 text-[12px] opacity-80">{f}</p>)}
            </div>
          )}
          {!nurLesen && stand.nachAbschluss && (
            <div className={T.hinweisWarn} data-testid="abr-nach-abschluss">
              <p className="flex items-center gap-2 text-[13px] font-semibold"><Icon name="sync_problem" className="text-[18px]" /> Nach dem Abschluss {stand.nachAbschluss.differenzCents > 0 ? `${money(stand.nachAbschluss.differenzCents)} zusätzlich eingegangen` : `${money(-stand.nachAbschluss.differenzCents)} weniger (Erstattung)`}</p>
              <p className="mt-0.5 text-[12.5px]">
                Eingefroren: bezahlt {money(stand.nachAbschluss.eingefrorenBezahltCents)}{stand.nachAbschluss.eingefroreneUeberzahlungCents > 0 ? `, Überzahlung ${money(stand.nachAbschluss.eingefroreneUeberzahlungCents)}` : ""}. Aktuell: bezahlt {money(stand.nachAbschluss.aktuellBezahltCents)}
                {stand.nachAbschluss.aktuelleUeberzahlungCents > 0 ? `, Überzahlung ${money(stand.nachAbschluss.aktuelleUeberzahlungCents)}` : stand.nachAbschluss.aktuellOffenCents > 0 ? `, offen ${money(stand.nachAbschluss.aktuellOffenCents)}` : ", nichts offen"}.
                {" "}Die Rechnung bleibt, wie sie ist. {stand.nachAbschluss.aktuelleUeberzahlungCents > stand.nachAbschluss.eingefroreneUeberzahlungCents ? "Vorschlag: den zusätzlichen Betrag erstatten (Übersicht → Zahlung → Erstatten) oder, wenn sich die Leistung geändert hat, korrigieren." : "Vorschlag: prüfen und bei geänderter Leistung korrigieren."}
              </p>
              <div className="mt-2 flex flex-wrap gap-3 text-[12.5px] font-semibold">
                {stand.korrektur?.moeglich && <button type="button" className="text-primary hover:underline" onClick={() => setKorrekturOffen(true)}>Korrigieren</button>}
                <Link to={vorgangPfad(eventId)} className="text-primary hover:underline">Zahlungen in der Übersicht</Link>
              </div>
            </div>
          )}
          {busy === "abschliessen" && (
            <div className={T.hinweisWarn}>
              <p className="flex items-center gap-2 font-semibold"><Icon name="hourglass_top" className="text-[18px]" /> Rechnung wird in LexOffice angelegt …</p>
              <p className="mt-1 text-[12.5px]">Der Knopf bleibt gesperrt, bis LexOffice antwortet. Ein zweiter Klick legt keinen zweiten Beleg an.</p>
            </div>
          )}
          {!stand.checkliste.anschrift && !gesperrt && (
            <div className={`${T.hinweisWarn} flex items-start gap-3`} data-testid="abr-anschrift">
              <Icon name="location_off" className="mt-0.5 text-[18px]" />
              <div className="min-w-0 flex-1">
                <p className="font-semibold">Rechnungsanschrift fehlt</p>
                <p className="text-[12.5px]">Ohne Anschrift kann die Rechnung nicht erstellt werden (§ 14 Abs. 4 UStG). Eintragen geht jetzt schon.</p>
              </div>
              {stand.kunde && <Link to={`/kunden/${stand.kunde.id}`} className="shrink-0 font-semibold text-primary hover:underline">Ergänzen</Link>}
            </div>
          )}
          {rechnung?.ungeklaert && (
            <div className={T.hinweisWarn}>
              <p className="flex items-center gap-2 font-semibold"><Icon name="help" className="text-[18px]" /> Unklar, ob die Rechnung in LexOffice angelegt wurde</p>
              <p className="mt-1 text-[12.5px]">LexOffice hat nicht rechtzeitig geantwortet. MAESTRO versucht es nicht von selbst noch einmal, sonst könnten zwei Rechnungen entstehen. Bitte in LexOffice unter „Rechnungen“ nach {money(stand.saldo?.rechnungsbetragCents ?? 0)} suchen.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <ButtonLink variant="secondary" href="https://app.lexoffice.de/vouchers#!/VoucherList/?filter=invoices" target="_blank" rel="noopener"><Icon name="open_in_new" className="text-[16px]" /> LexOffice öffnen</ButtonLink>
                {leitung && <Button variant="secondary" disabled={busy != null} onClick={ungewissFreigeben}><Icon name="lock_open" className="text-[16px]" /> Nicht vorhanden – freigeben</Button>}
              </div>
            </div>
          )}
          {gesperrt && rechnung && !rechnung.ungeklaert && !rechnung.sentAt && stand.mail && (
            <div id="abr-mail" className={mTab === "abschluss" ? "" : "hidden lg:block"}>
              <MailKarte stand={stand} busy={busy != null} bestaetigen={bestaetigen} setFehler={setFehler} setStand={(s) => { setStand(s); onGeaendert?.(); }} eventId={eventId} />
            </div>
          )}
          {rechnung?.sentAt && (
            <div className="rounded-[14px] border border-outline-variant bg-[#F0EAE0] px-4 py-3 text-[#6F675A] dark:border-white/10 dark:bg-white/[0.08] dark:text-[#b8ae9e]">
              <p className="flex items-center gap-2 font-semibold"><Icon name="check" className="text-[18px]" /> Rechnung {rechnung.invoiceNumber} gesendet · {formatDateTime(rechnung.sentAt)}</p>
              <p className="mt-0.5 text-[12.5px]">Mit PDF im Anhang. Die Mail steht im Gespräch des Vorgangs.</p>
            </div>
          )}

          <div className={mTab === "zeilen" ? "" : "hidden lg:block"}>
            <ZeilenKarte stand={stand} gesperrt={gesperrt} busy={busy} rufe={rufe} speichereZeilen={speichereZeilen} setDrawer={setDrawer} bestaetigen={bestaetigen} />
          </div>
          {!nurLesen && <div ref={zahlungenRef} className={mTab === "zahlungen" ? "" : "hidden lg:block"}>
            <ZahlungenKarte stand={stand} busy={busy} rufe={rufe} eventId={eventId} bestaetigen={bestaetigen} setFehler={setFehler} onAusserhalb={() => setDrawer({ art: "ausserhalb" })} />
          </div>}
        </div>
        <div className={`space-y-5 lg:sticky lg:top-[80px] ${mTab === "abschluss" ? "" : "hidden lg:block"}`}>
          {summenKarte}
          {abschluss}
        </div>
      </div>

      {/* Handy: Summenleiste unten fest, ein Knopf. */}
      <div className="fixed inset-x-0 bottom-[calc(64px+env(safe-area-inset-bottom))] z-30 border-t border-[#EFE8DA] bg-white/95 px-4 py-2.5 backdrop-blur lg:hidden dark:border-outline/30 dark:bg-night-card/95" data-testid="abr-summenleiste">
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-[18px] font-bold tabular-nums text-primary">{stand.ergebnis ? money(stand.ergebnis.summeCents) : "Noch nicht rechenbar"}</p>
            <p className="truncate text-[11px] text-secondary">
              {stand.ergebnis
                ? `${ueber > 0 ? `Überzahlung ${money(ueber)} · ` : ""}USt ${stand.ergebnis.jeSatz.filter((b) => b.rateBp > 0).map((b) => `${satzText(b.rateBp)}: ${money(b.vatCents)}`).join(" · ")}`
                : "Erst den Getränkeanteil festlegen"}
            </p>
          </div>
          {gesperrt ? (
            rechnung && !rechnung.sentAt && stand.mail ? <Button onClick={() => setMTab("abschluss")} className="!py-2">Mail freigeben</Button> : null
          ) : (
            <Button onClick={() => (offen ? setMTab("abschluss") : anlegenStarten())} disabled={!leitung || busy != null} className="!py-2">
              <Icon name="lock" className="text-[16px]" /> Abschließen
            </Button>
          )}
        </div>
      </div>

      {drawer?.art === "rabatt" && (
        <RabattDrawer stand={stand} zeile={drawer.zeile} zuschlag={drawer.zuschlag} busy={busy === "zeilen"} onClose={() => setDrawer(null)} onSpeichern={async (z) => {
          const liste = stand.zeilen.map(alsEingabe);
          if (drawer.zeile) liste.splice(liste.findIndex((x) => x.id === drawer.zeile!.id), 1, z);
          else liste.splice(einfuegeIndexRabatt(stand.zeilen), 0, z);
          if (await speichereZeilen(liste)) setDrawer(null);
        }} />
      )}
      {drawer?.art === "betrag" && (
        <BetragsDrawer stand={stand} zeile={drawer.zeile} art={drawer.typ} wo={drawer.wo} busy={busy === "zeilen"} onClose={() => setDrawer(null)} onSpeichern={async (z, index) => {
          const liste = stand.zeilen.map(alsEingabe);
          if (drawer.zeile) liste.splice(liste.findIndex((x) => x.id === drawer.zeile!.id), 1, z);
          else liste.splice(index ?? liste.length, 0, z);
          if (await speichereZeilen(liste)) setDrawer(null);
        }} />
      )}
      {korrekturOffen && <KorrekturDialog stand={stand} busy={busy === "korrigieren"} onClose={() => setKorrekturOffen(false)} onKorrigieren={korrigieren} />}
      {drawer?.art === "ausserhalb" && (
        <AusserhalbDrawer stand={stand} busy={busy === "ausserhalb"} onClose={() => setDrawer(null)} onSpeichern={async (b) => { if (await rufe("ausserhalb", "PUT", "/ausserhalb", b)) setDrawer(null); }} />
      )}
      {drawer?.art === "vorschau" && (
        <VorschauDrawer storno={stand.stornoBeimAbschluss ?? null} daten={vorschau.daten} fehler={vorschau.fehler} laden={vorschau.laden} kunde={stand.kunde?.name ?? ""} darf={leitung} busy={busy === "abschliessen"} onClose={() => setDrawer(null)} onAnlegen={inLexofficeAnlegen} />
      )}
    </div>
  );
}

function FehlerBlock({ fehler, onRetry }: { fehler: { text: string; code?: string; kundeId?: string }; onRetry?: () => void }) {
  return (
    <div className={T.hinweisGefahr} role="alert" data-testid="abr-fehler">
      <p className="flex items-center gap-2 font-semibold"><Icon name="error" className="text-[18px]" /> {fehler.code === "lexoffice_nicht_erreichbar" ? "Rechnung nicht angelegt: LexOffice nicht erreichbar" : fehler.code === "send_failed" ? "Die Mail kam nicht an" : fehler.code === "storno_ungeklaert" ? "Storno-Antwort unklar, bitte in LexOffice prüfen" : fehler.code === "storno_fehlgeschlagen" ? "Storno der bisherigen Rechnung gescheitert – keine neue Rechnung erstellt" : "Das hat nicht geklappt"}</p>
      <p className="mt-1 text-[12.5px]">{fehler.text}</p>
      <div className="mt-2 flex flex-wrap gap-3 text-[12.5px] font-semibold">
        {fehler.kundeId && <Link to={`/kunden/${fehler.kundeId}`} className="text-primary hover:underline">Anschrift im Kundenprofil ergänzen</Link>}
        {onRetry && <button type="button" className="text-primary hover:underline" onClick={onRetry}>Erneut versuchen</button>}
      </div>
    </div>
  );
}

/* ─────────────────────────────── Zeilen ─────────────────────────────── */

function SortierZeile({ id, aus, children }: { id: string; aus: boolean; children: (griff: ReactNode) => ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: aus });
  const griff = aus ? null : (
    <button type="button" className="cursor-grab touch-none text-[#B8AE9E] hover:text-on-surface-variant" aria-label="Zeile verschieben" {...attributes} {...listeners}>
      <Icon name="drag_indicator" className="text-[18px]" />
    </button>
  );
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }}>
      {children(griff)}
    </div>
  );
}

function ZeilenKarte({ stand, gesperrt, busy, rufe, speichereZeilen, setDrawer, bestaetigen }: {
  stand: AbrAntwort;
  gesperrt: boolean;
  busy: string | null;
  rufe: (was: string, m: "GET" | "POST" | "PUT", p: string, b?: unknown) => Promise<AbrAntwort | null>;
  speichereZeilen: (z: AbrZeileEingabe[]) => Promise<AbrAntwort | null>;
  setDrawer: (d: { art: "rabatt"; zeile: AbrZeile | null; zuschlag?: boolean } | { art: "betrag"; zeile: AbrZeile | null; typ: "leistung" | "mehrverbrauch"; wo?: "ueber" | "unter" }) => void;
  bestaetigen: ReturnType<typeof useBestaetigung>["bestaetigen"];
}) {
  const abr = stand.abrechnung!;
  const [gaeste, setGaeste] = useState(String(abr.gaeste));
  const [pp, setPp] = useState(abr.preisProPersonCents != null ? (abr.preisProPersonCents / 100).toLocaleString("de-DE", { minimumFractionDigits: 2 }) : "");
  const [anteil, setAnteil] = useState(String((abr.getraenkeanteilBp ?? 3000) / 100));
  const [umstellung, setUmstellung] = useState<{ text: string; vorher: AbrZeileEingabe[] } | null>(null);
  const [anteilOffen, setAnteilOffen] = useState(false);
  useEffect(() => setGaeste(String(abr.gaeste)), [abr.gaeste]);
  useEffect(() => setPp(abr.preisProPersonCents != null ? (abr.preisProPersonCents / 100).toLocaleString("de-DE", { minimumFractionDigits: 2 }) : ""), [abr.preisProPersonCents]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const ergebnisVon = useMemo(() => new Map((stand.ergebnis?.zeilen ?? []).map((e) => [e.id, e])), [stand.ergebnis]);
  const proPerson = stand.zeilen.some((z) => z.proPerson);
  const hatAnteile = stand.zeilen.some((z) => z.anteil);
  const angebotPp = abr.angebotGaeste && abr.angebotSummeCents != null && abr.angebotGaeste > 0 ? Math.round(abr.angebotSummeCents / abr.angebotGaeste) : null;
  const ppAenderbar = hatAnteile || stand.zeilen.filter((z) => z.art === "leistung" && z.proPerson).length === 1;

  // Konzept § 7 „Reihenfolge-Verwirrung“: nach dem Ziehen zeigt MAESTRO, um wie viel sich der Betrag
  // ändert, mit Rückgängig (stellt die vorige Reihenfolge wieder her). Beide Zahlen rechnet der Server.
  const verschieben = async (ev: DragEndEvent) => {
    if (!ev.over || ev.active.id === ev.over.id) return;
    const alt = stand.zeilen.findIndex((z) => z.id === ev.active.id);
    const neu = stand.zeilen.findIndex((z) => z.id === ev.over!.id);
    const vorherListe = stand.zeilen.map(alsEingabe);
    const r = await speichereZeilen(arrayMove(stand.zeilen, alt, neu).map(alsEingabe));
    const text = r ? umordnungsHinweis(stand, r) : null;
    setUmstellung(text ? { text, vorher: vorherListe } : null);
  };
  const rueckgaengig = async () => {
    if (!umstellung) return;
    if (await speichereZeilen(umstellung.vorher)) setUmstellung(null);
  };
  const loeschen = async (z: AbrZeile) => {
    if (!(await bestaetigen({ titel: "Zeile entfernen?", text: `„${zeilenTitel(z)}“ wird aus der Abrechnung entfernt.`, bestaetigen: "Entfernen", gefahr: true }))) return;
    await speichereZeilen(stand.zeilen.filter((x) => x.id !== z.id).map(alsEingabe));
  };
  const gaesteSpeichern = () => {
    const n = Number(gaeste);
    if (Number.isInteger(n) && n >= 0 && n !== abr.gaeste) void rufe("gaeste", "PUT", "", { gaeste: n });
  };
  const ppSpeichern = () => {
    const c = parseGermanEurosToCents(pp);
    if (c != null && c >= 0 && c !== abr.preisProPersonCents) void rufe("pp", "PUT", "", { preisProPersonCents: c });
  };

  const anteilZeilen = stand.zeilen.filter((z) => z.anteil);
  const paketSumme = anteilZeilen.reduce((s, z) => s + (ergebnisVon.get(z.id)?.bruttoCents ?? 0), 0);
  const fehlerId = stand.fehler?.zeileId;
  const rabattDa = stand.zeilen.some((z) => z.art === "rabatt");
  const ohneAufteilung = abr.ohneAufteilung;
  const anteilBp = Math.round(Number(anteil.replace(",", ".")) * 100);
  const altfallZeile = stand.zeilen.find((z) => z.quelle === "angebot" && z.steuersatzBp == null && z.art === "leistung");
  const basisPreis = altfallZeile ? (altfallZeile.einzelpreisCents ?? 0) : (abr.preisProPersonCents ?? 0);
  const basisMenge = altfallZeile ? (altfallZeile.proPerson ? abr.gaeste : 1) : abr.gaeste;
  const av = Number.isFinite(anteilBp) && anteilBp >= 0 && anteilBp <= 10000 ? anteilVorschau(basisPreis, basisMenge, anteilBp) : null;

  return (
    <>
      {(ohneAufteilung || anteilOffen) && !gesperrt && (
        <div className={`${T.hinweisWarn} mb-5`} data-testid="abr-altfall">
          <p className="flex items-center gap-2 font-semibold"><Icon name="call_split" className="text-[18px]" /> Pauschale ohne Aufteilung: Getränkeanteil einmal festlegen</p>
          <p className="mt-1 text-[12.5px]">Das Angebot hat keine einzelnen Positionen. Speisen gelten mit 7 %, Getränke mit 19 %. Wie viel vom Preis pro Person sind Getränke?</p>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="block">
              <span className="mb-1 block text-[12px] font-semibold">Getränkeanteil %</span>
              <Input className="!w-24" inputMode="decimal" value={anteil} onChange={(e) => setAnteil(e.target.value)} aria-label="Getränkeanteil in Prozent" />
            </label>
            <span className="pb-2 text-[11.5px]">vorbelegt mit 30 %, änderbar</span>
          </div>
          {av && (
            <div className="mt-3 overflow-x-auto rounded-[10px] bg-white/70 p-3 text-[12px] text-on-background dark:bg-night-card/60 dark:text-inverse-on-surface">
              <table className="w-full min-w-[360px]">
                <thead className="text-[10.5px] uppercase tracking-[0.07em] text-secondary"><tr><th className="text-left">Anteil</th><th className="text-right">Brutto</th><th className="text-right">Netto</th><th className="text-right">USt</th></tr></thead>
                <tbody className="tabular-nums">
                  <tr><td>Speisen {prozentText(10000 - anteilBp)} · 7 %</td><td className="text-right">{money(av.speisen.brutto)}</td><td className="text-right">{money(av.speisen.netto)}</td><td className="text-right">{money(av.speisen.ust)}</td></tr>
                  <tr><td>Getränke {prozentText(anteilBp)} · 19 %</td><td className="text-right">{money(av.getraenke.brutto)}</td><td className="text-right">{money(av.getraenke.netto)}</td><td className="text-right">{money(av.getraenke.ust)}</td></tr>
                  <tr className="font-semibold"><td>Gesamt</td><td className="text-right">{money(av.gesamt.brutto)}</td><td className="text-right">{money(av.gesamt.netto)}</td><td className="text-right">{money(av.gesamt.ust)}</td></tr>
                </tbody>
              </table>
            </div>
          )}
          <Button className="mt-3" disabled={!av || busy != null} onClick={() => void rufe("anteil", "PUT", "", { getraenkeanteilBp: anteilBp }).then((r) => r && setAnteilOffen(false))}>
            <Icon name="check" className="text-[18px]" /> Aufteilung übernehmen
          </Button>
          <p className="mt-1 text-[11.5px]">Danach als zwei Zeilen sichtbar und über „Anteil ändern“ jederzeit änderbar.</p>
        </div>
      )}

      <section className={T.karte} data-testid="abr-zeilen">
        <header className={T.kopf}>
          <Icon name="receipt_long" className="text-[18px] text-primary" />
          <h3 className={T.titel}>Leistungen, Rabatte, Mehrverbrauch</h3>
          <Chip ton="neutral">Version {abr.version}</Chip>
          {abr.angebotSummeCents != null && (
            <span className={`ml-auto ${T.leise}`}>
              Angebot: {money(abr.angebotSummeCents)}{abr.angebotGaeste && angebotPp ? ` · ${abr.angebotGaeste} × ${money(angebotPp)}` : ""}
              {/* KÜ V2 (S3): Grundlage ist die gültige Kostenübernahme — steht so auch auf der Rechnung. */}
              {stand.grundlage && (
                <span className="block" data-testid="abr-grundlage">
                  Grundlage: {stand.grundlage.nummer}{stand.grundlage.signedAt ? ` vom ${formatDate(stand.grundlage.signedAt)}` : ""}
                </span>
              )}
            </span>
          )}
        </header>

        {proPerson && (
          <div className="border-b border-[#F2ECDF] bg-[#FFFDF8] px-[18px] py-3 dark:border-outline/30 dark:bg-surface-variant/10">
            <p className="mb-2 text-[10.5px] font-semibold uppercase tracking-[0.07em] text-[#9A9080]">Gilt für alle Zeilen „pro Person“</p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[12.5px]">
              <label className="flex items-center gap-2">
                <span className="text-on-surface-variant dark:text-secondary-fixed-dim">Finale Gäste</span>
                {gesperrt ? <b className="tabular-nums">{abr.gaeste}</b> : (
                  <Input className={`!w-20 !py-1.5 ${abr.angebotGaeste != null && abr.gaeste !== abr.angebotGaeste ? "!border-primary !ring-2 !ring-primary/20" : ""}`} inputMode="numeric" value={gaeste} onChange={(e) => setGaeste(e.target.value.replace(/\D/g, ""))} onBlur={gaesteSpeichern} onKeyDown={(e) => e.key === "Enter" && gaesteSpeichern()} aria-label="Finale Gäste" />
                )}
              </label>
              {abr.angebotGaeste != null && <span className={T.leise}>Angebot: {abr.angebotGaeste}</span>}
              <span className="text-[#C9BFAC]">·</span>
              <label className="flex items-center gap-2">
                <span className="text-on-surface-variant dark:text-secondary-fixed-dim">Finaler Preis pro Person</span>
                {gesperrt || !ppAenderbar ? <b className="tabular-nums">{money(abr.preisProPersonCents)}</b> : (
                  <Input className={`!w-28 !py-1.5 ${angebotPp != null && abr.preisProPersonCents !== angebotPp ? "!border-primary !ring-2 !ring-primary/20" : ""}`} inputMode="decimal" value={pp} onChange={(e) => setPp(e.target.value)} onBlur={ppSpeichern} onKeyDown={(e) => e.key === "Enter" && ppSpeichern()} aria-label="Finaler Preis pro Person" />
                )}
              </label>
              {angebotPp != null && <span className={T.leise}>Angebot: {money(angebotPp)}</span>}
            </div>
            {!ppAenderbar && !gesperrt && <p className={`mt-1 ${T.leise}`}>Der Preis pro Person verteilt sich auf mehrere Zeilen – bitte in der jeweiligen Zeile ändern.</p>}
          </div>
        )}

        {(anteilZeilen.length > 0 || altfallZeile) && abr.paketTitel && (
          <div className={`px-[18px] pt-4 pb-2 ${T.zeile}`}>
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${PILL_CLASSES.primary}`}><Icon name="inventory_2" className="text-[13px]" /> aus Angebot übernommen</span>
                <p className="mt-2 font-headline-sm text-[16px] font-bold text-on-background dark:text-inverse-on-surface">{abr.paketTitel}</p>
                {abr.paketText && <p className="mt-1 text-[12.5px] text-on-surface-variant dark:text-secondary-fixed-dim">{abr.paketText}</p>}
              </div>
              <div className="text-right">
                <p className="text-[19px] font-bold tabular-nums text-on-background dark:text-inverse-on-surface">{money(anteilZeilen.length ? paketSumme : ergebnisVon.get(altfallZeile?.id ?? "")?.bruttoCents ?? (altfallZeile ? (altfallZeile.einzelpreisCents ?? 0) * (altfallZeile.proPerson ? abr.gaeste : 1) : 0))}</p>
                <p className={T.leise}>{abr.gaeste} Gäste × {money(abr.preisProPersonCents)}</p>
              </div>
            </div>
          </div>
        )}

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={verschieben}>
          <SortableContext items={stand.zeilen.map((z) => z.id)} strategy={verticalListSortingStrategy}>
            {stand.zeilen.map((z, i) => {
              const e = ergebnisVon.get(z.id);
              const naechste = stand.zeilen[i + 1];
              const zwischensumme = (z.art === "rabatt" || z.art === "zuschlag") && naechste && naechste.art !== "rabatt" && naechste.art !== "zuschlag";
              const chips = (
                <>
                  {istBetragszeile(z) && z.steuersatzBp != null && (wirdRabattiert(stand.zeilen, i) ? <Chip ton="neutral">rabattiert</Chip> : rabattDa ? <Chip ton="neutral">ohne Rabatt</Chip> : null)}
                  {istBetragszeile(z) && z.steuersatzBp != null && <Chip ton={z.steuersatzBp === 700 ? "success" : "info"}>{satzText(z.steuersatzBp)}</Chip>}
                  {!istBetragszeile(z) && e && e.jeSatz.length > 0 && <Chip ton="neutral">{e.jeSatz.map((s) => s.rateBp / 100).join(" / ")} %</Chip>}
                </>
              );
              return (
                <SortierZeile key={z.id} id={z.id} aus={gesperrt || busy != null}>
                  {(griff) => (
                    <>
                      <div className={`${T.zeile} flex items-start gap-2 px-[18px] py-2.5 hover:bg-[#FBF6EC] dark:hover:bg-surface-variant/15 ${fehlerId === z.id ? "bg-[#F8E7E3] dark:bg-[#B23B32]/15" : ""} ${z.anteil ? "pl-8" : ""}`} data-testid="abr-zeile">
                        <span className="w-[18px] shrink-0 pt-0.5">{griff}</span>
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] font-semibold text-on-background dark:text-inverse-on-surface">
                            {zeilenTitel(z)}{z.anteil && abr.getraenkeanteilBp != null ? ` · ${prozentText(z.anteil === "getraenke" ? abr.getraenkeanteilBp : 10000 - abr.getraenkeanteilBp)}` : ""}
                          </p>
                          <p className={T.leise}>
                            {istBetragszeile(z) ? mengeText(z, abr.gaeste) : rabattUnterzeile(z, e, stand.zeilen)}
                            {z.beschreibung && !z.anteil ? ` · ${z.beschreibung}` : ""}
                            {z.anteil === "getraenke" && !gesperrt && (
                              <> · <button type="button" className="font-semibold text-primary hover:underline" onClick={() => setAnteilOffen(true)}>Anteil ändern</button></>
                            )}
                          </p>
                          <div className="mt-1 flex flex-wrap gap-1.5 sm:hidden">{chips}</div>
                          {z.steuersatzBp == null && istBetragszeile(z) && <p className="mt-0.5 flex items-center gap-1 text-[11.5px] font-semibold text-[#6F675A]"><Icon name="warning" className="text-[14px]" /> Steuersatz fehlt: Speisen- und Getränkeanteil noch nicht getrennt.</p>}
                        </div>
                        <div className="hidden shrink-0 flex-wrap items-center justify-end gap-1.5 pt-0.5 sm:flex">{chips}</div>
                        <span className={`w-[84px] shrink-0 sm:w-[92px] pt-0.5 text-right text-[13px] ${T.betrag}`}>{e ? money(e.bruttoCents) : "–"}</span>
                        {!gesperrt && (
                          <span className="flex shrink-0 gap-0.5">
                            {!z.anteil && (
                              <button type="button" aria-label="Bearbeiten" className="rounded p-0.5 text-on-surface-variant hover:text-primary" onClick={() => (istBetragszeile(z) ? setDrawer({ art: "betrag", zeile: z, typ: z.art === "leistung" ? "leistung" : "mehrverbrauch" }) : setDrawer({ art: "rabatt", zeile: z }))}>
                                <Icon name="edit" className="text-[16px]" />
                              </button>
                            )}
                            <button type="button" aria-label="Entfernen" className="rounded p-0.5 text-on-surface-variant hover:text-error" onClick={() => void loeschen(z)}>
                              <Icon name="delete" className="text-[16px]" />
                            </button>
                          </span>
                        )}
                      </div>
                      {zwischensumme && (
                        <div className={`${T.zeile} flex justify-between bg-[#FBF8F1] px-[18px] py-2 pl-[44px] text-[11px] font-semibold uppercase tracking-[0.07em] text-[#9A9080] dark:bg-surface-variant/15`}>
                          <span>Zwischensumme</span>
                          <span className={`text-[13px] normal-case tracking-normal ${T.betrag}`}>{money(stand.laufend[z.id] ?? 0)}</span>
                        </div>
                      )}
                    </>
                  )}
                </SortierZeile>
              );
            })}
          </SortableContext>
        </DndContext>

        {umstellung && (
          <div className="flex flex-wrap items-center gap-2 border-b border-[#F6F0E4] bg-[#F0EAE0] px-[18px] py-2 text-[12px] text-[#6F675A] dark:border-outline/20 dark:bg-white/[0.08] dark:text-[#b8ae9e]" role="status" data-testid="abr-umordnung">
            <Icon name="swap_vert" className="text-[16px]" /> <span className="min-w-0 flex-1">{umstellung.text}</span>
            <button type="button" className="font-semibold hover:underline disabled:opacity-50" disabled={busy != null} onClick={() => void rueckgaengig()}>Rückgängig</button>
            <button type="button" className="font-semibold hover:underline" onClick={() => setUmstellung(null)}>Ok</button>
          </div>
        )}
        {stand.fehler && stand.checkliste.steuersaetze && <div className="px-[18px] pt-3"><ErrorNote message={stand.fehler.meldung} /></div>}

        {gesperrt ? (
          <p className={`flex items-center gap-2 px-[18px] py-3 ${T.leise}`}><Icon name="lock" className="text-[15px]" /> Abgeschlossen am {formatDate(abr.abgeschlossenAt)}{abr.abgeschlossenVon ? ` von ${abr.abgeschlossenVon}` : ""}. {stand.nurLesen ? "Diese Version ist abgelöst." : `Ändern geht nur noch über „Korrigieren“ (Gutschrift und Version ${abr.version + 1}).`}</p>
        ) : (
          <div className="flex flex-wrap items-center gap-2 px-[18px] py-3">
            <span className={T.leise}>Hinzufügen</span>
            {rabattDa ? (
              <Aufklapper ausrichtung="links" ausloeser={({ umschalten }) => (<Button variant="secondary" className="!py-1.5" onClick={umschalten}><Icon name="add" className="text-[16px]" /> Leistung <Icon name="expand_more" className="text-[16px]" /></Button>)}>
                {(zu) => (
                  <>
                    <p className="px-3.5 pb-1 pt-2 text-[10.5px] font-bold uppercase tracking-[0.08em] text-secondary-fixed-dim">Wo einfügen?</p>
                    <MenueEintrag icon="vertical_align_top" onClick={() => { zu(); setDrawer({ art: "betrag", zeile: null, typ: "leistung", wo: "ueber" }); }}>Über dem Rabatt <span className="block text-[11px] text-secondary">wird rabattiert</span></MenueEintrag>
                    <MenueEintrag icon="vertical_align_bottom" onClick={() => { zu(); setDrawer({ art: "betrag", zeile: null, typ: "leistung", wo: "unter" }); }}>Unter dem Rabatt <span className="block text-[11px] text-secondary">ohne Rabatt, wie Mehrverbrauch</span></MenueEintrag>
                    <p className="px-3.5 pb-2 pt-1 text-[11px] text-secondary">Später jederzeit per Griff verschiebbar.</p>
                  </>
                )}
              </Aufklapper>
            ) : (
              <Button variant="secondary" className="!py-1.5" onClick={() => setDrawer({ art: "betrag", zeile: null, typ: "leistung", wo: "ueber" })}><Icon name="add" className="text-[16px]" /> Leistung</Button>
            )}
            <Button variant="secondary" className="!py-1.5" onClick={() => setDrawer({ art: "rabatt", zeile: null })}><Icon name="add" className="text-[16px]" /> Rabatt</Button>
            <Button variant="secondary" className="!py-1.5" onClick={() => setDrawer({ art: "rabatt", zeile: null, zuschlag: true })}><Icon name="add" className="text-[16px]" /> Zuschlag</Button>
            <Button variant="secondary" className="!py-1.5" onClick={() => setDrawer({ art: "betrag", zeile: null, typ: "mehrverbrauch" })}><Icon name="add" className="text-[16px]" /> Mehr-/Minderverbrauch</Button>
          </div>
        )}

        {stand.ergebnis && (
          <div className="rounded-b-[14px] bg-[#FBF8F1] px-[18px] py-3 dark:bg-surface-variant/15">
            <div className="flex justify-between text-[12px] text-on-surface-variant dark:text-secondary-fixed-dim"><span>Leistungen</span><span className="tabular-nums">{money(stand.ergebnis.leistungenCents)}</span></div>
            {stand.ergebnis.rabattCents !== 0 && <div className="flex justify-between text-[12px] text-on-surface-variant dark:text-secondary-fixed-dim"><span>Rabatt</span><span className="tabular-nums">{money(stand.ergebnis.rabattCents)}</span></div>}
            {stand.ergebnis.zuschlagCents !== 0 && <div className="flex justify-between text-[12px] text-on-surface-variant dark:text-secondary-fixed-dim"><span>Zuschlag</span><span className="tabular-nums">{money(stand.ergebnis.zuschlagCents)}</span></div>}
            <div className="mt-2 flex items-end justify-between gap-3 border-t border-outline-variant/40 pt-2">
              <div>
                <p className="text-[14.5px] font-semibold text-on-background dark:text-inverse-on-surface">Rechnungsbetrag</p>
                <p className={T.leise}>inkl. USt {stand.ergebnis.jeSatz.filter((b) => b.rateBp > 0).map((b) => `${satzText(b.rateBp)} ${money(b.vatCents)}`).join(" · ")} · netto {money(stand.ergebnis.jeSatz.reduce((s, b) => s + b.netCents, 0))}</p>
              </div>
              <div className="text-right">
                <p className="text-[19px] font-bold tabular-nums text-primary" data-testid="abr-summe">{money(stand.ergebnis.summeCents)}</p>
                {stand.proGastCents != null && <p className={T.leise}>{money(stand.proGastCents)} pro Gast</p>}
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  );
}

/* ─────────────────────────────── Zahlungen ─────────────────────────────── */

function ZahlungenKarte({ stand, busy, rufe, eventId, bestaetigen, setFehler, onAusserhalb }: {
  stand: AbrAntwort;
  busy: string | null;
  rufe: (was: string, m: "GET" | "POST" | "PUT", p: string, b?: unknown) => Promise<AbrAntwort | null>;
  eventId: string;
  bestaetigen: ReturnType<typeof useBestaetigung>["bestaetigen"];
  setFehler: (f: { text: string } | null) => void;
  onAusserhalb: () => void;
}) {
  const abr = stand.abrechnung!;
  const leitung = stand.darfAbschliessen;
  const offen = stand.saldo?.offenCents ?? 0;
  // N1: entschieden wird erst, wenn die Rechnung aus dieser Abrechnung fertig angelegt ist.
  const belegFertig = abr.status === "abgeschlossen" && Boolean(stand.rechnung && stand.rechnung.hatLexBeleg && !stand.rechnung.ungeklaert && (stand.rechnung.status === "open" || stand.rechnung.status === "paid"));
  const gesperrtK = belegFertig;
  // B1: vor dem Abschluss nur anzeigen; entschieden wird danach, auf dem eingefrorenen Betrag.
  const ueber = gesperrtK ? (stand.ueberzahlungZuEntscheidenCents ?? 0) : (stand.saldo?.ueberzahlungCents ?? 0);
  const [erfassen, setErfassen] = useState(false);
  const [weg, setWeg] = useState<"ec_karte" | "ueberweisung" | "bar">("ec_karte");
  const [betrag, setBetrag] = useState("");
  const [datum, setDatum] = useState(heuteInZone("Europe/Berlin"));
  const [beleg, setBeleg] = useState("");
  const [entscheidung, setEntscheidung] = useState<"rueckerstattung" | "guthaben">("rueckerstattung");
  const [rWeg, setRWeg] = useState<"ueberweisung" | "bar" | "stripe">("ueberweisung");
  const [grund, setGrund] = useState("");
  const pille = zahlungsPille(stand);
  const stripeMoeglich = stand.zahlungen.some((p) => p.status === "succeeded" && p.stripeBezahlt);
  const entschieden = Boolean(abr.ueberzahlung);
  const offeneStripe = stand.zahlungen.filter((p) => (p.status === "pending" || p.status === "processing") && p.zahlweise === "stripe");

  useEffect(() => { if (erfassen) setBetrag(offen > 0 ? (offen / 100).toLocaleString("de-DE", { minimumFractionDigits: 2 }) : ""); }, [erfassen, offen]);

  const speichern = async () => {
    const c = parseGermanEurosToCents(betrag);
    if (!c || c <= 0) return setFehler({ text: "Bitte einen Betrag über 0 eintragen." });
    if (await rufe("zahlung", "POST", "/zahlungen", { zahlweise: weg, betragCents: c, datum, beleg: beleg.trim() || null })) {
      setErfassen(false);
      setBeleg("");
    }
  };
  const toast = useToastWennVorhanden();
  const stripeLink = async () => {
    if (!(await bestaetigen({ titel: "Stripe-Link senden?", text: `Der Gast bekommt per Mail einen Zahlungslink über den offenen Betrag von ${money(offen)}.`, bestaetigen: "Link senden" }))) return;
    try {
      const z = await apiMutate<{ paymentId: string }>(`/api/events/${eventId}/abrechnung/stripe-zahlung`, "POST", {});
      await apiMutate(`/api/payments/${z.paymentId}/checkout-link`, "POST", {}).catch(() => undefined);
      const antwort = await apiMutate<{ to?: string } | undefined>(`/api/payments/${z.paymentId}/send-link`, "POST", {});
      toast.success(antwort?.to ? `Zahlungslink an ${antwort.to} gesendet` : "Zahlungslink gesendet");
      await rufe("laden", "GET", "");
      setErfassen(false);
    } catch (e) {
      setFehler({ text: fehlerText(e) });
    }
  };

  const zeilen: { key: string; icon: string; titel: string; marke?: string; unter: string; betrag: number; pill: { label: string; tone: keyof typeof PILL_CLASSES } }[] = [
    ...stand.anzahlungsrechnungen.map((a) => ({ key: `a-${a.id}`, icon: "receipt", titel: `Anzahlungsrechnung ${a.nummer ?? ""}`.trim(), marke: a.quelle === "extern" ? "außerhalb erfasst" : undefined, unter: `${a.datum ? formatDate(a.datum) : ""} · wird in der Schlussrechnung je Steuersatz abgezogen`, betrag: -a.vatBreakdown.reduce((s, b) => s + b.grossCents, 0), pill: { label: "abgerechnet", tone: "neutral" as const } })),
    ...stand.eingaenge.map((e) => ({
      key: `e-${e.id}`,
      icon: e.quelle === "guthaben" ? "savings" : zahlweise(e.zahlweise).icon,
      // Stufe 2: eine in einer früheren Version als Guthaben geführte Überzahlung ist Geld, das diesen
      // Vorgang schon verlassen hat (negativer Eingang) — nicht „verrechnet“.
      titel: e.rueckerstattung ? `Rückerstattung aus Version ${e.version}` : e.quelle === "guthaben" ? (e.betragCents < 0 ? "Als Guthaben übertragen" : "Guthaben verrechnet") : zahlweise(e.zahlweise).text,
      marke: e.quelle === "extern" ? "außerhalb erfasst" : e.version != null ? `aus Version ${e.version}` : undefined,
      unter: [e.datum ? `${e.quelle === "guthaben" ? "Gebucht" : "Eingang"} ${formatDate(e.datum)}` : null, e.beleg ? `Beleg ${e.beleg}` : null, e.rueckerstattung ? "entschieden, zählt als Abgang" : e.quelle === "guthaben" && e.betragCents < 0 ? "Überzahlung steht beim Kunden als Guthaben" : null].filter(Boolean).join(" · "),
      betrag: e.betragCents,
      pill: e.rueckerstattung ? { label: "Erstattung", tone: "warn" as const } : e.quelle === "guthaben" && e.betragCents < 0 ? { label: "Guthaben", tone: "info" as const } : { label: "Bezahlt", tone: "success" as const },
    })),
    ...offeneStripe.map((p) => ({ key: `s-${p.id}`, icon: "link", titel: "Stripe-Link", unter: p.hatLink ? "Link gesendet, Zahlung offen" : "Zahlung angelegt", betrag: p.amountCents, pill: { label: "Offen", tone: "warn" as const } })),
    ...(entschieden && abr.ueberzahlung?.entscheidung === "rueckerstattung"
      ? [{ key: "rueck", icon: "undo", titel: `Rückerstattung per ${abr.ueberzahlung.weg === "bar" ? "bar" : abr.ueberzahlung.weg === "stripe" ? "Stripe" : "Überweisung"}`, unter: `entschieden ${formatDate(abr.ueberzahlung.am)} · Überzahlung`, betrag: -(abr.ueberzahlung.cents ?? 0), pill: { label: "Geplant", tone: "warn" as const } }]
      : []),
  ];

  return (
    <section className={T.karte} data-testid="abr-zahlungen">
      <header className={T.kopf}>
        <h3 className={T.titel}>Zahlungen</h3>
        <span className="ml-auto"><StatusPill meta={{ label: pille.text, tone: pille.ton }} /></span>
      </header>
      <div className="space-y-2.5 px-[18px] py-4">
        {zeilen.length === 0 && <p className="text-[12.5px] text-on-surface-variant dark:text-secondary-fixed-dim">Noch keine Zahlung erfasst. Vor dem Abschluss fragt MAESTRO nach Zahlungen außerhalb von MAESTRO.</p>}
        {zeilen.map((z) => (
          <div key={z.key} className="flex items-center gap-3 rounded-lg border border-outline-variant/40 p-2.5 dark:border-outline/30">
            <span className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-[9px] bg-surface-container-high text-secondary dark:bg-surface-variant/20"><Icon name={z.icon} className="text-[18px]" /></span>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold text-on-background dark:text-inverse-on-surface">{z.titel} {z.marke && <Chip ton="neutral">{z.marke}</Chip>}</p>
              <p className={T.leise}>{z.unter}</p>
            </div>
            <div className="shrink-0 text-right">
              <p className={`text-[13px] ${T.betrag}`}>{money(z.betrag)}</p>
              <StatusPill meta={z.pill} />
            </div>
          </div>
        ))}

        {abr.status === "entwurf" && (
          <button type="button" onClick={onAusserhalb} className="flex w-full items-center gap-2 rounded-lg border border-dashed border-outline-variant/60 px-3 py-2 text-left text-[12.5px] text-on-surface-variant hover:border-primary hover:text-primary dark:border-outline/40 dark:text-secondary-fixed-dim">
            <Icon name={abr.ausserhalbBeantwortet == null ? "help" : "check_circle"} className="text-[17px]" />
            {abr.ausserhalbBeantwortet == null ? "Pflichtfrage: Zahlungen oder Rechnungen außerhalb von MAESTRO?" : abr.ausserhalbBeantwortet ? "Außerhalb von MAESTRO erfasst – ändern" : "Außerhalb von MAESTRO: nichts – ändern"}
          </button>
        )}

        {abr.status === "entwurf" && leitung && stand.guthaben.saldoCents + stand.guthaben.verrechnetCents > 0 && stand.guthaben.entstandenCents === 0 && (
          <label className="flex items-center gap-2 rounded-lg bg-[#F0EAE0] px-3 py-2 text-[12.5px] text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]">
            <Checkbox checked={stand.guthaben.verrechnetCents > 0} disabled={busy != null} onChange={(e) => void rufe("guthaben", "POST", "/guthaben", { verrechnen: e.target.checked })} />
            Guthaben des Kunden verrechnen ({money(stand.guthaben.saldoCents + stand.guthaben.verrechnetCents)} verfügbar). Mindert den offenen Betrag, nicht den Rechnungsbetrag.
          </label>
        )}

        {leitung && !erfassen && (
          <Button variant="secondary" className="!py-1.5" onClick={() => setErfassen(true)}><Icon name="add" className="text-[16px]" /> Zahlung erfassen</Button>
        )}
        {!leitung && <p className={T.leise}>Zahlungen verbuchen dürfen Inhaber.</p>}

        {erfassen && (
          <div className="rounded-[12px] border border-outline-variant/40 p-4 dark:border-outline/30" data-testid="abr-zahlung-erfassen">
            <p className="mb-3 text-[10.5px] font-semibold uppercase tracking-[0.07em] text-[#9A9080]">Zahlung erfassen</p>
            <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
              {([
                { k: "stripe", icon: "link", t: "Stripe-Link senden", u: offen > 0 ? `Link über ${money(offen)}` : "Nichts offen: kein Link nötig", aus: offen <= 0 },
                { k: "ec_karte", icon: "credit_card", t: "EC-Karte vor Ort", u: "Betrag, Datum, Terminal-Beleg" },
                { k: "ueberweisung", icon: "account_balance", t: "Überweisung", u: "Betrag, Eingangsdatum" },
                { k: "bar", icon: "payments", t: "Bar", u: "Betrag, Datum, Quittung" },
              ] as const).map((kachel) => (
                <button
                  key={kachel.k}
                  type="button"
                  disabled={"aus" in kachel && kachel.aus}
                  onClick={() => (kachel.k === "stripe" ? void stripeLink() : setWeg(kachel.k))}
                  className={`rounded-[12px] border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${weg === kachel.k ? "border-primary bg-primary/[0.05]" : "border-outline-variant/50 hover:border-primary/60 dark:border-outline/30"}`}
                >
                  <span className="flex items-center gap-1.5 text-[12.5px] font-semibold text-on-background dark:text-inverse-on-surface"><Icon name={kachel.icon} className="text-[16px] text-primary" /> {kachel.t}</span>
                  <span className="mt-0.5 block text-[11px] text-secondary dark:text-secondary-fixed-dim">{kachel.u}</span>
                </button>
              ))}
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              <label className="block"><span className="mb-1 block text-[12px] font-semibold text-on-surface-variant">Betrag</span><Input inputMode="decimal" value={betrag} onChange={(e) => setBetrag(e.target.value)} placeholder="0,00 €" aria-label="Betrag" /></label>
              <label className="block"><span className="mb-1 block text-[12px] font-semibold text-on-surface-variant">Datum</span><DateInput value={datum} onValueChange={setDatum} aria-label="Datum" /></label>
              <label className="block"><span className="mb-1 block text-[12px] font-semibold text-on-surface-variant">{weg === "ec_karte" ? "Terminal-Beleg (optional)" : weg === "bar" ? "Quittung (optional)" : "Verwendungszweck (optional)"}</span><Input value={beleg} onChange={(e) => setBeleg(e.target.value)} placeholder={weg === "ec_karte" ? "z. B. 004711" : ""} aria-label="Beleg" /></label>
            </div>
            <div className="mt-3 rounded-[10px] bg-[#F0EAE0] p-3 text-[12px] text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]">
              <p className="flex items-center gap-1.5 font-semibold"><Icon name="call_split" className="text-[16px]" /> Gemischt zahlen</p>
              <p className="mt-0.5">Jede Zahlung ist eine eigene Zeile mit eigener Zahlweise, etwa 3.000 € Überweisung und der Rest per EC.</p>
            </div>
            <div className="mt-3 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setErfassen(false)}>Abbrechen</Button>
              <Button disabled={busy != null} onClick={() => void speichern()}><Icon name="check" className="text-[18px]" /> Zahlung speichern</Button>
            </div>
          </div>
        )}

        {(stand.offeneLinks ?? []).length > 0 && (
          <div className={T.hinweisWarn} data-testid="abr-offene-links">
            <p className="flex items-center gap-2 font-semibold"><Icon name="link_off" className="text-[18px]" /> Offene Zahlungslinks aus dem Zahlungsplan</p>
            <p className="mt-1 text-[12.5px]">
              {(stand.offeneLinks ?? []).map((l) => `${money(l.amountCents)}${l.dueDate ? ` (fällig ${formatDate(l.dueDate)})` : ""}${l.status === "processing" ? " – Zahlung läuft gerade" : ""}`).join(", ")} – der Gast könnte darüber noch zahlen. {abr.status === "abgeschlossen" ? "Beim Abschluss wurden alte Links deaktiviert; diese ließen sich nicht deaktivieren – bitte in der Übersicht prüfen." : "Beim Abschließen und beim Anlegen des Restlinks deaktiviert MAESTRO sie automatisch (eine laufende Zahlung bleibt unangetastet)."}
            </p>
          </div>
        )}

        {ueber > 0 && !entschieden && !gesperrtK && (
          <div className={T.hinweisGefahr} data-testid="abr-ueberzahlung">
            <p className="flex items-center gap-2 font-semibold"><Icon name="error" className="text-[18px]" /> Überzahlung {money(ueber)}</p>
            <p className="mt-1 text-[12.5px]">Gezahlt {money(stand.saldo?.bezahltCents ?? 0)}, Rechnungsbetrag {money(stand.ergebnis?.summeCents ?? 0)}. Der Abschluss geht trotzdem. Rückerstattung oder Guthaben entscheidest du, sobald die Rechnung fertig in LexOffice angelegt ist – erst dann steht der Betrag fest. Die Mail an den Gast geht erst danach.</p>
          </div>
        )}

        {ueber > 0 && !entschieden && gesperrtK && (
          <div className="space-y-3 pt-2" data-testid="abr-ueberzahlung">
            <div className={T.hinweisGefahr}>
              <p className="flex items-center gap-2 font-semibold"><Icon name="error" className="text-[18px]" /> Überzahlung {money(ueber)}: Rückerstattung oder Guthaben?</p>
              <p className="mt-1 text-[12.5px]">Gezahlt {money(stand.saldo?.bezahltCents ?? 0)}, Rechnungsbetrag {money(stand.ergebnis?.summeCents ?? 0)}. Der Betrag ist mit dem Abschluss festgeschrieben. Die Mail an den Gast geht erst, wenn das entschieden ist.</p>
            </div>
            {leitung ? (
              <>
                <label className={`block cursor-pointer rounded-[12px] border p-3.5 ${entscheidung === "rueckerstattung" ? "border-primary" : "border-outline-variant/50 dark:border-outline/30"}`}>
                  <span className="flex items-center gap-2 text-[13px] font-semibold"><Radio name="ueber" checked={entscheidung === "rueckerstattung"} onChange={() => setEntscheidung("rueckerstattung")} /> Zurückerstatten</span>
                  {entscheidung === "rueckerstattung" && (
                    <div className="mt-2 space-y-2 pl-6">
                      <p className={T.leise}>Weg wählen.{stripeMoeglich ? "" : " Keine Zahlung kam über Stripe, eine Stripe-Erstattung ist deshalb nicht möglich."}</p>
                      <div className="max-w-[360px]"><Segmente label="Weg" wert={rWeg} onWahl={setRWeg} werte={[{ key: "ueberweisung", label: "Überweisung" }, { key: "bar", label: "Bar" }, { key: "stripe", label: "Stripe", aus: !stripeMoeglich }]} /></div>
                    </div>
                  )}
                </label>
                <label className={`block cursor-pointer rounded-[12px] border p-3.5 ${entscheidung === "guthaben" ? "border-primary" : "border-outline-variant/50 dark:border-outline/30"}`}>
                  <span className="flex items-center gap-2 text-[13px] font-semibold"><Radio name="ueber" checked={entscheidung === "guthaben"} onChange={() => setEntscheidung("guthaben")} /> Als Guthaben beim Kunden führen</span>
                  <span className={`mt-1 block pl-6 ${T.leise}`}>Steht im Kundenprofil als Guthaben {money(ueber)} und wird bei der nächsten Buchung verrechnet. Die Buchung klärt der Steuerberater.</span>
                </label>
                <label className="block"><span className="mb-1 block text-[12px] font-semibold text-on-surface-variant">Begründung *</span><Input value={grund} onChange={(e) => setGrund(e.target.value)} maxLength={1000} placeholder="z. B. Abrechnung niedriger als die Vorauszahlung" aria-label="Begründung" /></label>
                <div className="flex justify-end">
                  <Button disabled={grund.trim().length < 3 || busy != null} onClick={() => void rufe("ueberzahlung", "PUT", "/ueberzahlung", { entscheidung, ...(entscheidung === "rueckerstattung" ? { weg: rWeg } : {}), begruendung: grund.trim() })}>
                    <Icon name="check" className="text-[18px]" /> Entscheidung speichern
                  </Button>
                </div>
              </>
            ) : (
              <p className={T.leise}>Entscheiden dürfen Inhaber.</p>
            )}
          </div>
        )}
        {entschieden && abr.ueberzahlung?.begruendung && <p className={T.leise}>Begründung: {abr.ueberzahlung.begruendung}</p>}
      </div>
    </section>
  );
}

/* ─────────────────────────────── rechte Spalte ─────────────────────────────── */

function SummenKarte({ stand }: { stand: AbrAntwort }) {
  const e = stand.ergebnis;
  const s = stand.saldo;
  const ueber = s?.ueberzahlungCents ?? 0;
  const u = stand.abrechnung?.ueberzahlung;
  const entschieden = Boolean(u);
  return (
    <section className={`${T.karte} p-[18px]`} data-testid="abr-summenkarte">
      <div className="flex items-start justify-between gap-2">
        <p className={T.titel}>Rechnungsbetrag</p>
        <p className={`text-right ${T.leise}`}>{stand.abrechnung?.gaeste} Gäste</p>
      </div>
      <div className="mt-1 flex items-baseline justify-between gap-2">
        <p className="text-[24px] font-bold tabular-nums text-primary">{e ? money(e.summeCents) : <span className="text-[13px] font-medium text-secondary">noch nicht rechenbar</span>}</p>
        <p className={T.leise}>vom Programm gerechnet</p>
      </div>
      {e && (
        <div className="mt-3 rounded-[10px] bg-[#FBF8F1] p-3 dark:bg-surface-variant/15">
          <table className="w-full text-[12px] tabular-nums">
            <thead className="text-[10px] uppercase tracking-[0.07em] text-[#9A9080]"><tr><th className="text-left">€</th><th className="text-right">Brutto</th><th className="text-right">Netto</th><th className="text-right">USt</th></tr></thead>
            <tbody className="text-on-surface-variant dark:text-secondary-fixed-dim">
              {e.jeSatz.map((b) => (
                <tr key={b.rateBp}><td>{satzText(b.rateBp)}</td><td className="text-right">{euroZahl(b.grossCents)}</td><td className="text-right">{euroZahl(b.netCents)}</td><td className="text-right font-semibold text-on-background dark:text-inverse-on-surface">{euroZahl(b.vatCents)}</td></tr>
              ))}
              <tr className="border-t border-outline-variant/40 font-semibold text-on-background dark:text-inverse-on-surface"><td className="pt-1 font-normal text-on-surface-variant">Summe</td><td className="pt-1 text-right">{euroZahl(e.summeCents)}</td><td className="pt-1 text-right">{euroZahl(e.jeSatz.reduce((x, b) => x + b.netCents, 0))}</td><td className="pt-1 text-right">{euroZahl(e.jeSatz.reduce((x, b) => x + b.vatCents, 0))}</td></tr>
            </tbody>
          </table>
        </div>
      )}
      {s && (
        <div className="mt-3 space-y-1 text-[12.5px]">
          <div className="flex justify-between text-on-surface-variant dark:text-secondary-fixed-dim"><span>Anzahlungsrechnungen</span><span className="tabular-nums">{money(0 - stand.anzahlungsrechnungen.reduce((x, a) => x + a.vatBreakdown.reduce((y, b) => y + b.grossCents, 0), 0) || 0)}</span></div>
          {stand.eingaenge.map((z) => (
            <div key={z.id} className="flex justify-between text-on-surface-variant dark:text-secondary-fixed-dim"><span>{z.rueckerstattung ? `Rückerstattung (V${z.version})` : z.quelle === "guthaben" ? (z.betragCents < 0 ? `Als Guthaben übertragen${z.version != null ? ` (V${z.version})` : ""}` : "Guthaben verrechnet") : zahlweise(z.zahlweise).text}{z.datum ? ` ${formatDate(z.datum).slice(0, 6)}` : ""}</span><span className="tabular-nums">{money(-z.betragCents)}</span></div>
          ))}
          {stand.eingaenge.length === 0 && <div className="flex justify-between text-on-surface-variant dark:text-secondary-fixed-dim"><span>Zahlungseingänge</span><span className="tabular-nums">{money(0)}</span></div>}
          <div className={`flex justify-between border-t border-outline-variant/40 pt-1.5 font-semibold ${ueber > 0 ? "text-error" : "text-on-background dark:text-inverse-on-surface"}`}>
            <span>{ueber > 0 ? "Überzahlung" : "Offen"}</span><span className="tabular-nums">{money(ueber > 0 ? ueber : s.offenCents)}</span>
          </div>
          {u && <div className="flex justify-between text-on-surface-variant dark:text-secondary-fixed-dim"><span>{u.entscheidung === "guthaben" ? "Guthaben beim Kunden" : "Rückerstattung geplant"}</span><span className="tabular-nums">{money(-ueber)}</span></div>}
        </div>
      )}
      {ueber > 0 && !entschieden && (
        <div className={`mt-3 ${T.hinweisGefahr}`}>
          <p className="flex items-center gap-2 text-[13px] font-semibold"><Icon name="error" className="text-[17px]" /> Überzahlung {money(ueber)}</p>
          <p className="mt-0.5 text-[12px]">{stand.abrechnung?.status === "abgeschlossen" ? "Rückerstattung oder Guthaben entscheiden, dann die Mail freigeben." : "Sperrt den Abschluss nicht. Entschieden wird nach dem Abschluss."}</p>
        </div>
      )}
      {stand.abrechnung?.angebotSummeCents != null && e && (
        <div className="mt-3 rounded-[10px] bg-primary/[0.07] p-3 text-[12.5px] dark:bg-primary/15">
          <p className="text-on-background dark:text-inverse-on-surface">Angebot → Abrechnung</p>
          <p className="font-semibold tabular-nums text-primary">{money(stand.abrechnung.angebotSummeCents)} → {money(e.summeCents)} · {(stand.abweichungZumAngebotCents ?? 0) >= 0 ? "+" : ""}{money(stand.abweichungZumAngebotCents ?? 0)}</p>
        </div>
      )}
    </section>
  );
}

function PunktZeile({ ok, warn, titel, text, aktion }: { ok: boolean; warn?: boolean; titel: string; text?: ReactNode; aktion?: ReactNode }) {
  return (
    <li className="flex gap-2.5 border-b border-[#F6F0E4] py-2.5 last:border-0 dark:border-outline/20">
      <Icon name={ok ? "check_circle" : warn ? "info" : "radio_button_unchecked"} className={`mt-0.5 text-[17px] ${ok ? "text-[#6F675A] dark:text-[#b8ae9e]" : warn ? "text-[#6F675A]" : "text-[#9A9080]"}`} />
      <div className="min-w-0 flex-1">
        <p className="text-[12.5px] font-medium text-on-background dark:text-inverse-on-surface">{titel}</p>
        {text && <p className={T.leise}>{text}</p>}
      </div>
      {aktion && <span className="shrink-0 text-[12.5px] font-semibold text-primary">{aktion}</span>}
    </li>
  );
}

function AbschlussKarte({ stand, busy, onAusserhalb, onUeberzahlung, onAbschliessen }: { stand: AbrAntwort; busy: boolean; onAusserhalb: () => void; onUeberzahlung: () => void; onAbschliessen: () => void }) {
  const c = stand.checkliste;
  const abr = stand.abrechnung!;
  const ueber = stand.saldo?.ueberzahlungCents ?? 0;
  const offen = stand.offenePunkte.length;
  const grund = !stand.darfAbschliessen ? "Abschließen dürfen nur Inhaber." : offen > 0 ? `Gesperrt: ${offen} Pflichtpunkt${offen === 1 ? "" : "e"} offen` : null;
  return (
    <section className={`${T.karte} p-[18px]`} data-testid="abr-abschluss">
      <div className="flex items-center justify-between">
        <p className={T.titel}>Abschluss</p>
        {offen > 0 ? <StatusPill meta={{ label: `${offen} offen`, tone: "warn" }} /> : <StatusPill meta={{ label: "bereit", tone: "success" }} />}
      </div>
      <ul className="mt-2">
        <PunktZeile ok={c.steuersaetze && c.rechnung} titel="Jede Zeile hat einen Steuersatz" text={!c.steuersaetze ? "Getränkeanteil festlegen oder Steuersatz ergänzen" : stand.fehler?.meldung} />
        <PunktZeile
          ok={c.ausserhalb}
          titel="Außerhalb von MAESTRO geklärt"
          text={abr.ausserhalbBeantwortet == null ? "Pflichtfrage bei jedem Abschluss" : abr.ausserhalbBeantwortet ? `Ja: ${stand.extern.zahlungen.length} Zahlung(en), ${stand.extern.rechnungen.length} Rechnung(en) erfasst` : "Nein: alles in MAESTRO erfasst"}
          aktion={<button type="button" className="hover:underline" onClick={onAusserhalb}>{abr.ausserhalbBeantwortet == null ? "Beantworten" : "Ändern"}</button>}
        />
        <PunktZeile ok={c.anschrift} titel="Rechnungsanschrift vorhanden" text={c.anschrift ? undefined : `fehlt bei ${stand.kunde?.name ?? "dem Kunden"}`} aktion={!c.anschrift && stand.kunde ? <Link to={`/kunden/${stand.kunde.id}`} className="hover:underline">Ergänzen</Link> : undefined} />
        {!c.keinHauptbeleg && <PunktZeile ok={false} titel="Bisherige Rechnung in Bearbeitung" text="Bitte zuerst in der Übersicht klären." />}
        {stand.stornoBeimAbschluss && <PunktZeile ok warn titel={`Rechnung ${stand.stornoBeimAbschluss.invoiceNumber ?? ""} wird beim Abschließen storniert`} text={`Gutschrift über ${money(stand.stornoBeimAbschluss.amountGrossCents)} in LexOffice, danach die neue Rechnung. Beide gehen zusammen in der Mail an den Gast.`} />}
        {ueber > 0 && <PunktZeile ok={false} warn titel={`Überzahlung ${money(ueber)}`} text="sperrt nicht; Rückerstattung oder Guthaben nach dem Abschluss, vor der Mail an den Gast" aktion={<button type="button" className="hover:underline" onClick={onUeberzahlung}>Ansehen</button>} />}
        {(stand.offeneLinks ?? []).length > 0 && <PunktZeile ok={false} warn titel="Offene Zahlungslinks im Zahlungsplan" text="sperrt nicht; bitte in der Übersicht deaktivieren" />}
      </ul>
      <Button className="mt-3 w-full justify-center" disabled={grund != null || busy} onClick={onAbschliessen} data-testid="abr-abschliessen">
        <Icon name="lock" className="text-[18px]" /> {busy ? "Wird angelegt …" : "Abrechnung abschließen"}
      </Button>
      {grund && <p className={`mt-2 flex items-center justify-center gap-1 text-center ${T.leise}`}><Icon name={stand.darfAbschliessen ? "lock" : "shield_person"} className="text-[14px]" /> {grund}</p>}
      {!stand.darfAbschliessen && <p className={`mt-1 text-center ${T.leise}`}>Eintragen kannst du alles; der Entwurf bleibt gespeichert.</p>}
    </section>
  );
}

function RechnungsKarte({ stand, onMail, onKorrigieren }: { stand: AbrAntwort; onMail: () => void; onKorrigieren: () => void }) {
  const r = stand.rechnung;
  const abr = stand.abrechnung!;
  return (
    <section className={`${T.karte} p-[18px]`} data-testid="abr-rechnung">
      <div className="flex items-center justify-between gap-2">
        <p className={T.titel}>Rechnung</p>
        {r ? <StatusPill meta={r.ungeklaert ? { label: "ungeklärt", tone: "warn" } : r.sentAt ? { label: "versendet", tone: "success" } : { label: "erstellt · nicht versendet", tone: "warn" }} /> : <StatusPill meta={{ label: "noch nicht angelegt", tone: "neutral" }} />}
      </div>
      {r && !r.ungeklaert && (
        <dl className="mt-3 grid grid-cols-[1fr_auto] gap-y-1 rounded-[10px] bg-[#FBF8F1] p-3 text-[12.5px] dark:bg-surface-variant/15">
          <dt className="text-on-surface-variant">Nummer</dt><dd className="text-right font-semibold">{r.invoiceNumber ?? "–"}</dd>
          <dt className="text-on-surface-variant">Rechnungsdatum</dt><dd className="text-right font-semibold">{formatDate(r.createdAt)}</dd>
          <dt className="text-on-surface-variant">Leistungsdatum</dt><dd className="text-right font-semibold">{formatDate(stand.event.eventDate)}</dd>
          <dt className="text-on-surface-variant">Betrag</dt><dd className="text-right font-semibold tabular-nums">{money(r.amountGrossCents)}</dd>
        </dl>
      )}
      {r && !r.ungeklaert && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          {r.pdfUrl ? <ButtonLink variant="secondary" className="justify-center" href={r.pdfUrl} target="_blank" rel="noopener"><Icon name="picture_as_pdf" className="text-[16px]" /> PDF</ButtonLink> : <Button variant="secondary" disabled>PDF</Button>}
          {r.lexUrl ? <ButtonLink variant="secondary" className="justify-center" href={r.lexUrl} target="_blank" rel="noopener"><Icon name="open_in_new" className="text-[16px]" /> LexOffice</ButtonLink> : <Button variant="secondary" disabled>LexOffice</Button>}
        </div>
      )}
      {stand.lexAbweichung && (
        <div className={`mt-3 ${T.hinweisGefahr}`} data-testid="abr-lex-abweichung">
          <p className="flex items-center gap-2 text-[13px] font-semibold"><Icon name="error" className="text-[17px]" /> LexOffice weicht ab</p>
          <p className="mt-0.5 text-[12px]">{stand.lexAbweichung}</p>
        </div>
      )}
      {r?.lexArt === "gutschrift" && <p className={`mt-2 ${T.leise}`}>Schlussrechnung mit Betrag zu Gunsten des Gastes – in LexOffice als Rechnungskorrektur angelegt.</p>}
      {r && !r.sentAt && !r.ungeklaert && stand.mail && (
        <Button className="mt-2 w-full justify-center" onClick={onMail}><Icon name="outgoing_mail" className="text-[18px]" /> Mail an Gast freigeben</Button>
      )}
      {!stand.nurLesen && (
        <>
          <Button variant="secondary" className="mt-2 w-full justify-center" disabled={!stand.korrektur?.moeglich} onClick={onKorrigieren} data-testid="abr-korrigieren">
            <Icon name="edit_note" className="text-[16px]" /> Korrigieren
          </Button>
          <p className={`mt-2 flex gap-1.5 ${T.leise}`}>
            <Icon name="info" className="text-[14px]" />
            {stand.korrektur?.moeglich || !stand.korrektur?.grund ? `Legt Version ${abr.version + 1} an. Beim Abschließen storniert MAESTRO diese Rechnung per Gutschrift und erstellt die neue; beide gehen nach deiner Freigabe zusammen an den Gast.` : stand.korrektur.grund}
          </p>
        </>
      )}
    </section>
  );
}

/* ─────────────────────────────── Korrigieren ─────────────────────────────── */

function KorrekturDialog({ stand, busy, onClose, onKorrigieren }: { stand: AbrAntwort; busy: boolean; onClose: () => void; onKorrigieren: (grund: string) => void }) {
  const [grund, setGrund] = useState("");
  const abr = stand.abrechnung!;
  const r = stand.rechnung;
  return (
    <Modal
      open
      onClose={onClose}
      width="520px"
      title={`Abrechnung korrigieren – Version ${abr.version + 1}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Abbrechen</Button>
          <Button disabled={grund.trim().length < 3 || busy} onClick={() => onKorrigieren(grund.trim())} data-testid="abr-korrigieren-bestaetigen">
            <Icon name="edit_note" className="text-[18px]" /> {busy ? "Legt an …" : `Version ${abr.version + 1} anlegen`}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-[13px] text-on-background dark:text-inverse-on-surface">
        <p>Eine nummerierte Rechnung wird nie überschrieben. So läuft die Korrektur:</p>
        <ol className="list-decimal space-y-1 pl-5 text-[12.5px] text-on-surface-variant dark:text-secondary-fixed-dim">
          <li>MAESTRO legt Version {abr.version + 1} als Entwurf an, mit allen Zeilen und Angaben aus Version {abr.version}. Version {abr.version} bleibt lesbar.</li>
          <li>Du änderst, was nicht stimmt, und schließt Version {abr.version + 1} ab. Dabei wird {r?.invoiceNumber ? `Rechnung ${r.invoiceNumber}` : "die bisherige Rechnung"} per Gutschrift storniert und die neue Rechnung erstellt.</li>
          <li>Gutschrift und neue Rechnung gehen erst nach deiner Freigabe zusammen in einer Mail an den Gast.</li>
        </ol>
        {(stand.guthaben.entstandenCents > 0 || stand.guthaben.verrechnetCents > 0) && (
          <p className="rounded-[10px] bg-[#FBF8F1] p-2.5 text-[12px] dark:bg-surface-variant/15">Guthaben aus Version {abr.version} bleibt gebucht und zählt in Version {abr.version + 1} als Zahlungsverrechnung – nichts wird doppelt gebucht.</p>
        )}
        <label className="block">
          <span className="mb-1 block text-[12px] font-semibold text-on-surface-variant dark:text-secondary-fixed-dim">Was wird korrigiert? *</span>
          <Textarea rows={3} value={grund} onChange={(e) => setGrund(e.target.value)} maxLength={1000} placeholder="z. B. Gast meldet 10 Gäste weniger" aria-label="Grund der Korrektur" />
        </label>
      </div>
    </Modal>
  );
}

/* ─────────────────────────────── Tor 2 ─────────────────────────────── */

export function MailKarte({ stand, busy, bestaetigen, setFehler, setStand, eventId }: {
  stand: AbrAntwort;
  busy: boolean;
  bestaetigen: ReturnType<typeof useBestaetigung>["bestaetigen"];
  setFehler: (f: { text: string; code?: string } | null) => void;
  setStand: (s: AbrAntwort) => void;
  eventId: string;
}) {
  const mail = stand.mail!;
  const r = stand.rechnung!;
  const [an, setAn] = useState(stand.kunde?.email ?? "");
  const [betreff, setBetreff] = useState(mail.betreff);
  const [text, setText] = useState(mail.text);
  const [zu, setZu] = useState(false);
  const [sendet, setSendet] = useState(false);
  const [abweichungGrund, setAbweichungGrund] = useState("");
  const [kiLaeuft, setKiLaeuft] = useState(false);
  // Stufe 2: „Mit KI entwerfen“ — nur der Text; Beträge setzt das Programm über Platzhalter ein.
  const mitKi = async () => {
    setKiLaeuft(true);
    setFehler(null);
    try {
      const r = await apiMutate<{ text: string }>(`/api/events/${eventId}/abrechnung/mail/entwurf`, "POST", {});
      setText(r.text);
    } catch (e) {
      const a = e as ApiError;
      setFehler({ text: a.message, code: a.code });
    } finally {
      setKiLaeuft(false);
    }
  };
  const ueberOffen = !stand.checkliste.ueberzahlungEntschieden;
  const senden = async (trotzdem = false) => {
    setSendet(true);
    setFehler(null);
    try {
      const s = await apiMutate<AbrAntwort>(`/api/events/${eventId}/abrechnung/mail`, "POST", { an: an.trim() || undefined, betreff, text, trotzdem, ...(stand.lexAbweichung ? { abweichungBegruendung: abweichungGrund.trim() } : {}) });
      setStand(s);
    } catch (e) {
      const a = e as ApiError;
      if (a.code === "fremde_betraege" && !trotzdem) {
        const ok = await bestaetigen({ titel: "Beträge prüfen", text: a.message, bestaetigen: "Trotzdem senden" });
        if (ok) return senden(true);
      } else setFehler({ text: a.message, code: a.code });
    } finally {
      setSendet(false);
    }
  };
  if (zu) return <Button variant="secondary" onClick={() => setZu(false)}><Icon name="outgoing_mail" className="text-[16px]" /> Rechnungsmail freigeben</Button>;
  return (
    <section className={T.karte} data-testid="abr-mail">
      <header className={T.kopf}>
        <Icon name="outgoing_mail" className="text-[18px] text-primary" />
        <h3 className={T.titel}>Rechnungsmail freigeben</h3>
        <StatusPill meta={{ label: "erstellt · nicht versendet", tone: "warn" }} />
      </header>
      <div className="space-y-3 px-[18px] py-4">
        <p className="flex items-center gap-2 text-[12.5px] text-[#6F675A] dark:text-[#b8ae9e]"><Icon name="task_alt" className="text-[17px]" /> Rechnung {r.invoiceNumber} in LexOffice angelegt · {money(r.amountGrossCents)} · Leistung {formatDate(stand.event.eventDate)}. An den Gast ist noch nichts gegangen.</p>
        <div className="grid gap-3 sm:grid-cols-[80px_minmax(0,1fr)] sm:items-center">
          <span className="text-[12px] text-on-surface-variant">An</span>
          <Input type="email" value={an} onChange={(e) => setAn(e.target.value)} aria-label="Empfänger" />
          <span className="text-[12px] text-on-surface-variant">Betreff</span>
          <Input value={betreff} onChange={(e) => setBetreff(e.target.value)} aria-label="Betreff" />
        </div>
        <Textarea rows={11} value={text} onChange={(e) => setText(e.target.value)} aria-label="Mailtext" />
        <div className="flex flex-wrap items-center gap-2" data-testid="abr-mail-anhaenge">
          {(stand.gutschriften ?? []).filter((g) => !g.sentAt).map((g) => (
            <span key={g.id} className="inline-flex items-center gap-1.5 rounded-lg border border-outline-variant/50 px-2.5 py-1.5 text-[12px] dark:border-outline/30">
              <Icon name="picture_as_pdf" className="text-[16px] text-primary" /> Gutschrift-{g.invoiceNumber}.pdf <span className="text-secondary">· Storno {g.stornierteNummer}</span>
              {g.pdfUrl && <a href={g.pdfUrl} target="_blank" rel="noopener" className="ml-1 font-semibold text-primary hover:underline">ansehen</a>}
            </span>
          ))}
          <span className="inline-flex items-center gap-1.5 rounded-lg border border-outline-variant/50 px-2.5 py-1.5 text-[12px] dark:border-outline/30"><Icon name="picture_as_pdf" className="text-[16px] text-primary" /> {r.lexArt === "gutschrift" ? "Schlussrechnung" : "Rechnung"}-{r.invoiceNumber}.pdf</span>
          {r.pdfUrl && <a href={r.pdfUrl} target="_blank" rel="noopener" className="text-[12.5px] font-semibold text-primary hover:underline">PDF ansehen</a>}
        </div>
        <p className={`leading-[1.9] ${T.leise}`}>
          <Icon name="info" className="mr-1 align-[-3px] text-[14px]" />Vom Programm eingesetzte Werte:{" "}
          {mail.werte.map((w) => <span key={w} className="mr-1 whitespace-nowrap rounded bg-primary/10 px-1 font-semibold text-primary">{w}</span>)}
          Der Text drumherum ist änderbar. Kommt ein Betrag vor, der kein Programmwert ist, warnt MAESTRO vor dem Senden.
        </p>
        {stand.lexAbweichung && (
          <div className={T.hinweisGefahr}>
            <p className="font-semibold">LexOffice weicht von der Abrechnung ab – Tor 2 ist gesperrt.</p>
            <p className="mt-0.5 text-[12px]">{stand.lexAbweichung}</p>
            <label className="mt-2 block text-[12px] font-semibold">
              Bewusst übersteuern – Begründung *
              <Input className="mt-1" value={abweichungGrund} onChange={(e) => setAbweichungGrund(e.target.value)} maxLength={1000} aria-label="Begründung zum Übersteuern" />
            </label>
          </div>
        )}
        {ueberOffen && <p className="text-[12.5px] font-semibold text-error">Überzahlung {money(stand.saldo?.ueberzahlungCents ?? 0)}: erst Rückerstattung oder Guthaben entscheiden, dann freigeben.</p>}
        <div className="flex flex-wrap items-center gap-2 border-t border-[#F2ECDF] pt-3 dark:border-outline/30">
          <Button variant="secondary" className="!py-1.5" disabled={kiLaeuft || sendet} onClick={() => void mitKi()} data-testid="abr-mail-ki">
            <Icon name="auto_awesome" className="text-[16px]" /> {kiLaeuft ? "Entwirft …" : "Mit KI entwerfen"}
          </Button>
          <span className="flex-1" />
          <Button variant="secondary" onClick={() => setZu(true)}>Später</Button>
          <Button disabled={busy || sendet || ueberOffen || !stand.darfAbschliessen || (Boolean(stand.lexAbweichung) && abweichungGrund.trim().length < 3)} onClick={() => void senden()} data-testid="abr-mail-senden">
            <Icon name="send" className="text-[18px]" /> {sendet ? "Sendet …" : "Freigeben und senden"}
          </Button>
        </div>
        {!stand.darfAbschliessen && <p className={T.leise}>Freigeben dürfen Inhaber.</p>}
      </div>
    </section>
  );
}
